/* eslint-disable complexity */
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Page } from '../theme/elements/Page';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import _ from 'lodash';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FormCard, FormCardLayout } from 'theme/elements/Forms';
import FormCreatorComponent from 'components/FormCreatorComponent';
import { formatNumber } from 'components/FormCreatorComponent/helpers';
import {
	beneficiaryHyperSyncForm,
	customerDashboardNonEditableTypesForms,
	customerDashboardTypesForms,
} from 'sections/CustomerAccounts/forms.types';
import {
	getCompanyDashboard,
	getSuccessfullyUpdated,
	getLoading,
	setCustomerDashboard,
	getError as getCustomerAccountError,
	setError as setCustomerAccountError,
	setSuccessfullyUpdated,
	getSelectedEntity,
	setSelectedEntity,
} from 'state/reducers/customer-accounts.reducers';
import { hasPermission } from 'components/AuthorizationComponent';
import { permissionEnums } from 'constants/permission.constants';
import {
	getCustomerDashboard,
	getCompanyContactById,
	getCompanyBeneficiaryById,
	getCompanyBankAccountById,
	getCompanyPaymentById,
	getCompanyConversionById,
	getCompanyBalanceById,
	getCompanyBalanceRefundById,
	resendWelcomeEmail,
} from 'services/customer-accounts.service';
import { getFormsValuesByType } from 'sections/CustomerAccounts/helper';
import { FormButton } from 'theme/elements/Buttons';
import { Avatar, Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { customerAccountDetailsTypes, moneyActivityTypes } from 'constants/customer-account.constants';
import { validateFields } from 'components/FormCreator/validation';
import { BeneficiaryPaymentsTable } from 'sections/CustomerAccounts/BottomSection/BeneficiaryPaymentsTable';
import {
	editBankAccountData,
	editBeneficiaryData,
	editCompanyData,
	editContactData,
	editPaymentStatus,
} from 'services/companies.service';
import {
	getAltshulerEmployees,
	getError as getCompanyError,
	setError as setCompanyError,
} from 'state/reducers/company.reducers';
import { getError as getBalanceErrorMessage, setError as setBalanceError } from 'state/reducers/balance.reducer';
import { WarningDialog } from 'components/WarningDialog';
import {
	formatDate,
	formatJSDate,
	generalStatusEnum,
	generalTranslation,
	nonEditableHebrewStatus,
	nonEditableStatus,
} from 'constants/global.constants';
import { DialogBox } from 'components/DialogBox';
import TextArea from 'components/FormCreatorComponent/inputs/TextArea';
import { fieldType } from 'components/FormCreator/types';
import { fieldMode } from 'components/FormCreatorComponent/types';
import { getCountries, getCurrencies } from 'state/reducers/global.reducer';
import { companyIsNotActive, getCompanyNameByLanguage } from 'utils/index';
import { setSuccessMessage } from 'state/reducers/ui.reducers';
import { ResendWelcomeEmailDialog } from 'sections/pendingRequests/PendingRequestsDialogs';
import { ButtonComponent } from 'components/ButtonComponent';
import { CompanyBenefitsTable } from 'sections/CompanyBenefits';
import { getBenefits, getCompanyBenefitsByCompanyId } from 'services/benefits.service';
import { format, isValid } from 'date-fns';
import { createRefund, getHouseAccountBalances } from 'services/balances.service';
import { PopupComponent } from 'components/PopupComponent';
import { timing } from 'constants/ui.constants';
import { PaymentStatusHebrew } from 'constants/payment.constants';
import PaymentCategoryPicker from 'components/PaymentCategoryPicker';
import PaymentCategoriesView from 'components/PaymentCategoriesView';
import { getPaymentCategories, fetchPaymentCategoryTranslations } from 'services/payment-categories.service';
import {
	getPaymentCategoriesTranslationsMap,
	getPaymentCategoriesLoading,
	getPaymentCategoriesError,
	selectFlattenedCategoryOptions,
	setError as setPaymentCategoriesError,
} from 'state/reducers/payment-categories.reducer';

// ============================================================================
// CONSTANTS
// ============================================================================

const {
	CONTACTS,
	BENEFICIARIES,
	BANK_ACCOUNTS,
	PAYMENT,
	CONVERSION,
	BALANCE,
	BALANCE_WITHDRAWAL,
	BALANCE_REFUND,
	BASIC_DATA,
	PLAN,
} = customerAccountDetailsTypes;

const TYPE_TO_ENTITY_KEY: Record<string, string> = {
	[CONTACTS]: 'contacts',
	[BENEFICIARIES]: 'beneficiaries',
	[BANK_ACCOUNTS]: 'bankAccounts',
	[PAYMENT]: 'payment',
	[CONVERSION]: 'conversion',
	[BALANCE]: 'balance',
	[BALANCE_WITHDRAWAL]: 'balance',
	[BALANCE_REFUND]: 'balanceRefund',
	[BASIC_DATA]: 'basicData',
	[PLAN]: 'plan',
};

const ENTITY_FETCHERS: Record<string, (companyId: number, dataId: number) => any> = {
	[CONTACTS]: getCompanyContactById,
	[BENEFICIARIES]: getCompanyBeneficiaryById,
	[BANK_ACCOUNTS]: getCompanyBankAccountById,
	[PAYMENT]: getCompanyPaymentById,
	[CONVERSION]: getCompanyConversionById,
	[BALANCE]: getCompanyBalanceById,
	[BALANCE_WITHDRAWAL]: getCompanyBalanceById,
	[BALANCE_REFUND]: getCompanyBalanceRefundById,
};

const ACCOUNT_FIELDS = [
	{ form: 'manager.name', db: 'customerSuccessManagerId', key: 'altshulerManagers' },
	{ form: 'dealer.name', db: 'dealerId', key: 'altshulerDealers' },
	{ form: 'agent.name', db: 'agentId', key: 'altshulerAgents' },
	{ form: 'lead.name', db: 'leadId', key: 'altshulerLeads' },
];

const PAYMENT_CATEGORY_TYPES = [BENEFICIARIES, BASIC_DATA, PAYMENT];

// ============================================================================
// HELPERS
// ============================================================================

const formatCommentDate = (date: string, t: (k: string) => string) => {
	const d = new Date(date);
	return `${t('page.newAccount.tables.writtenAt')} - ${format(d, 'dd.MM.yyyy')} ${t(
		'page.newAccount.tables.atHour'
	)} ${format(d, 'HH:mm')}`;
};

const processRefund = (r: any) => ({
	ccId: r?.ccId,
	amount: r?.amount,
	reference: r?.reference,
	currencyId: r?.currency?.name,
	note: r?.note,
	reason: r?.reason,
	status: generalTranslation[r?.status] || r?.status,
	createdByBoUser: r?.createdByBoUser ? `${r.createdByBoUser.firstName} ${r.createdByBoUser.lastName}` : null,
	createdAt: isValid(new Date(r?.createdAt || '')) ? format(new Date(r.createdAt), timing.FULLDATE) : r?.createdAt,
	settlementDate: isValid(new Date(r?.settlementDate || ''))
		? format(new Date(r.settlementDate), timing.FULLDATE)
		: r?.settlementDate,
});

const transformKeys = (fields: Record<string, any>) => {
	if (!fields) return;
	ACCOUNT_FIELDS.forEach(({ form, db }) => {
		if (form in fields) {
			fields[db] = fields[form];
			delete fields[form];
		}
	});
};

const toNumbers = (obj: Record<string, any>) => {
	const result = { ...obj };
	Object.entries(result).forEach(([k, v]) => {
		if (typeof v === 'string' && v.trim() && !v.startsWith('0') && !isNaN(Number(v))) {
			result[k] = Number(v);
		}
	});
	return result;
};

const shouldHideForm = (name: string, entityType: string, item: any) => {
	const rules: Record<string, Record<string, boolean>> = {
		[PLAN]: {
			'page.customerAccounts.forms.nextMonthPlan.details.title': _.isEmpty(
				_.get(item, 'nextMonthPlan.plan.name')
			),
		},
		[PAYMENT]: {
			'page.customerAccounts.forms.balance_refund.create_refund_title': !_.isEmpty(
				_.get(item, 'refund.createdAt')
			),
			'page.customerAccounts.forms.balance_refund.title': _.isEmpty(_.get(item, 'refund.createdAt')),
		},
	};
	return rules[entityType]?.[name] ?? false;
};

const isRejectionStatus = (fields: any) =>
	[fields?.statusHebrew, fields?.status].includes(generalStatusEnum.REJECTED) ||
	fields?.status === generalStatusEnum.DELETED;

const getStatusDisplay = (key: string, fields: any) => {
	const s = fields[key];
	return _.values(PaymentStatusHebrew).includes(s) ? s : generalTranslation[s] || '';
};

const getErrorSetter = (errors: { company: any; customer: any; balance: any; categories: any }) => {
	if (errors.company.exist) return setCompanyError;
	if (errors.customer.exist) return setCustomerAccountError;
	if (errors.balance.exist) return setBalanceError;
	if (errors.categories.exist) return setPaymentCategoriesError;
	return null;
};

// ============================================================================
// COMPONENT
// ============================================================================

export const CustomerAccountsProfileApp = () => {
	const t = useAppSelector(selectTranslations);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const theme = useTheme();
	const location = useLocation();
	const { companyId, type, dataId } = useParams();

	// Computed entity type (used throughout)
	const entityType = _.camelCase(type) as string;
	// Permissions
	const canViewPage = hasPermission([permissionEnums.CUSTOMER_ACCOUNTS]);
	const canEditCompany = hasPermission([permissionEnums.EDIT_COMPANY]);
	const canResendEmail = hasPermission([permissionEnums.RESEND_WELCOME_EMAIL]);
	const canCreateRefund = hasPermission([permissionEnums.BO_SIGNER_CREATE_REFUND]);
	const canViewBenefits = hasPermission([permissionEnums.VIEW_BENEFIT]);
	const canViewHouseAccountBalance = hasPermission([permissionEnums.HOUSE_ACCOUNT_BALANCE]);

	const statusPerms = {
		[CONTACTS]: hasPermission([permissionEnums.EDIT_CONTACT_STATUS]),
		[BANK_ACCOUNTS]: hasPermission([permissionEnums.EDIT_BANK_ACCOUNT_STATUS]),
		[BENEFICIARIES]: hasPermission([permissionEnums.EDIT_BENEFICIARY_STATUS]),
		[moneyActivityTypes.PAYMENT]: canCreateRefund,
	};

	// Redux state
	const companyData = useAppSelector(getCompanyDashboard);
	const loading = useAppSelector(getLoading);
	const balanceLoading = useAppSelector((s) => s.balance.isLoading);
	const benefitLoading = useAppSelector((s) => s.benefits.isLoading);
	const successfullyUpdated = useAppSelector(getSuccessfullyUpdated);
	const companyError = useAppSelector(getCompanyError);
	const balanceError = useAppSelector(getBalanceErrorMessage);
	const customerError = useAppSelector(getCustomerAccountError);
	const categoryError = useAppSelector(getPaymentCategoriesError);
	const countries = useAppSelector(getCountries);
	const currencies = useAppSelector(getCurrencies);
	const employees = useAppSelector(getAltshulerEmployees(true)) as any;
	const selectedEntity = useAppSelector(getSelectedEntity);
	const paymentCategoryOptions = useAppSelector(selectFlattenedCategoryOptions);
	const paymentCategoryLoading = useAppSelector(getPaymentCategoriesLoading);
	const paymentCategoryTranslationsMap = useAppSelector(getPaymentCategoriesTranslationsMap);
	const detailEntityKey = TYPE_TO_ENTITY_KEY[entityType];
	const isDetailType = !!detailEntityKey && ![BASIC_DATA, PLAN].includes(entityType as customerAccountDetailsTypes);
	const isSelectedEntityReady =
		isDetailType &&
		selectedEntity?.type === detailEntityKey &&
		selectedEntity?.data &&
		(!dataId || Number(selectedEntity?.data?.id) === Number(dataId));
	const isEntityLoading = isDetailType && !!dataId && !isSelectedEntityReady;

	// Local state
	const [item, setItem] = useState<Record<string, any>>();
	const [formState, setFormState] = useState<any>({ dirtyFields: {}, isValid: true });
	const [paymentDirty, setPaymentDirty] = useState<any>({});
	const [refundDirty, setRefundDirty] = useState<any>({});
	const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
	const [BoNotes, setBoNotes] = useState<string | null>(null);
	const [originalStatus, setOriginalStatus] = useState('');
	const [showResendBtn, setShowResendBtn] = useState(false);
	const [isRefundReady, setIsRefundReady] = useState(false);
	const [noDecimalCurrency, setNoDecimalCurrency] = useState(false);

	// Popups
	const [dialog, setDialog] = useState(false);
	const [refundPopup, setRefundPopup] = useState(false);
	const [statusPopup, setStatusPopup] = useState(false);
	const [feePopup, setFeePopup] = useState(false);
	const [emailDialog, setEmailDialog] = useState(false);
	const [approveEnabled, setApproveEnabled] = useState(false);

	// Refs
	const loadedProfile = useRef(false);
	const initialCategoryIds = useRef<string[]>([]);
	const lastFetchedEntityType = useRef<string | null>(null);

	// Derived values
	const isInactive = useMemo(() => companyIsNotActive(companyData?.basicData), [companyData]);
	const countryOpts = useMemo(() => countries?.map((c: any) => ({ value: c, label: c.name })), [countries]);

	const hasError = companyError.exist || customerError.exist || balanceError.exist || categoryError.exist;
	const errorSetter = getErrorSetter({
		company: companyError,
		customer: customerError,
		balance: balanceError,
		categories: categoryError,
	});
	const errorText = customerError?.text || customerError?.subText || balanceError?.text;

	const categoryDirty = useMemo(() => {
		const sorted = (arr: string[]) => [...arr].sort().join(',');
		return sorted(selectedCategoryIds) !== sorted(initialCategoryIds.current);
	}, [selectedCategoryIds]);

	// Employee options for dropdowns
	const employeeOpts = useMemo(() => {
		if (!employees) return {};
		const unassigned = { id: null, name: 'לא משויך לעובד', label: 'לא משויך לעובד', value: null, active: true };
		return ACCOUNT_FIELDS.reduce((acc, { form, key }) => {
			acc[form] = [...(employees[key] || []), unassigned];
			return acc;
		}, {} as Record<string, any[]>);
	}, [employees]);

	// Form values from entity
	const formValues = useMemo(() => {
		const key = TYPE_TO_ENTITY_KEY[entityType];
		if (!key) return undefined;

		if (isDetailType && !isSelectedEntityReady) {
			setItem(undefined);
			return undefined;
		}

		// From selected entity
		if (selectedEntity?.type === key && selectedEntity?.data) {
			const data = { ...selectedEntity.data };
			let canChangeStatus = statusPerms[type as keyof typeof statusPerms] ?? true;

			if (type === CONTACTS) setShowResendBtn(!data.lastLoginDate);
			if (data.dateOfBirth) data.dateOfBirth = formatDate(data.dateOfBirth);

			if (entityType === PAYMENT && data.associatedRefund) {
				data.refund = processRefund(data.associatedRefund);
				canChangeStatus = false;
			}

			setItem({ ...data, company_id: data.companyId });

			return getFormsValuesByType(
				data,
				type === BALANCE_WITHDRAWAL ? moneyActivityTypes.BALANCE : type,
				canChangeStatus,
				countryOpts,
				setRefundPopup,
				isRefundReady,
				noDecimalCurrency
			);
		}

		if (!companyData) return undefined;

		switch (key) {
			case BASIC_DATA:
				setItem({ ...(companyData.basicData || {}) });
				return getFormsValuesByType(
					companyData.basicData,
					type,
					true,
					countryOpts,
					setRefundPopup,
					isRefundReady,
					noDecimalCurrency
				);
			case PLAN:
				setItem({ ...(companyData.plan || {}) });
				return getFormsValuesByType(
					companyData.plan,
					type,
					true,
					countryOpts,
					setRefundPopup,
					isRefundReady,
					noDecimalCurrency
				);
			default:
				return undefined;
		}
	}, [
		selectedEntity,
		companyData?.basicData,
		companyData?.plan,
		entityType,
		countryOpts,
		isRefundReady,
		noDecimalCurrency,
		paymentCategoryTranslationsMap,
		isSelectedEntityReady,
	]);

	// Build form config
	const forms = useMemo(() => {
		if (!formValues || !_.has(customerDashboardTypesForms, entityType)) return undefined;

		const isLocked =
			nonEditableStatus.includes(formValues.status) ||
			(nonEditableHebrewStatus.includes(formValues.statusHebrew) && type !== moneyActivityTypes.PAYMENT) ||
			isInactive;

		const source =
			type === BALANCE_REFUND
				? customerDashboardTypesForms
				: isLocked
				? customerDashboardNonEditableTypesForms
				: customerDashboardTypesForms;
		const baseForms = _.get(source, entityType, []).filter(
			(f: any) => !shouldHideForm(f.name, entityType, formValues)
		);

		return baseForms.map((form: any) => ({
			...form,
			fieldsGroups: form.fieldsGroups.map((group: any) => ({
				...group,
				fields: group.fields.map((field: any) => ({
					...field,
					props: {
						...field.props,
						options:
							field.type === fieldType.select && employeeOpts[field.props.name]
								? employeeOpts[field.props.name]
								: field.props?.options || [],
					},
				})),
			})),
		}));
	}, [formValues, entityType, isInactive, employeeOpts]);

	// Check if save button should be disabled
	const saveDisabled = useMemo(() => {
		const noDirty = !formState.dirtyFields || !Object.keys(formState.dirtyFields).length;
		const basicDataNoDirty = entityType === BASIC_DATA ? noDirty && !categoryDirty : noDirty;
		return (
			(!formState.isValid && type !== moneyActivityTypes.PAYMENT) ||
			basicDataNoDirty ||
			loading ||
			type === moneyActivityTypes.CONVERSION
		);
	}, [formState, entityType, categoryDirty, loading]);

	// -------------------------------------------------------------------------
	// HANDLERS
	// -------------------------------------------------------------------------

	const handleSave = () => {
		// Validate
		const merged = _.merge({}, formValues, formState.dirtyFields);
		forms?.forEach((f: any) => f.fieldsGroups.forEach((g: any) => validateFields(g.fields, merged)));

		if (!companyId) return;
		const id = parseInt(dataId as string);

		switch (entityType) {
			case BASIC_DATA:
				transformKeys(formState.dirtyFields);
				dispatch(
					editCompanyData({
						id: parseInt(companyId),
						...formState.dirtyFields,
						...(categoryDirty ? { allowedPaymentCategoryIds: selectedCategoryIds.map(Number) } : {}),
					})
				);
				break;
			case CONTACTS:
				dispatch(editContactData({ contact: item, ...toNumbers(formState.dirtyFields) }));
				setFormState({ ...formState, dirtyFields: {} });
				break;
			case BANK_ACCOUNTS:
				dispatch(editBankAccountData({ id, ...formState.dirtyFields }));
				break;
			case BENEFICIARIES:
				dispatch(editBeneficiaryData({ id, ...formState.dirtyFields }));
				break;
			case PAYMENT:
				if (paymentDirty.statusHebrew) setStatusPopup(true);
				if (paymentDirty['fee.statusHebrew']) setFeePopup(true);
				break;
		}
	};

	const handleStatusChange = (isFee = false) => {
		const id = parseInt(dataId as string);
		if (feePopup && isFee) {
			dispatch(editPaymentStatus({ id, status: paymentDirty['fee.statusHebrew'], isFee: true }, !feePopup));
			setFeePopup(false);
		}
		if (statusPopup) {
			dispatch(editPaymentStatus({ id, status: paymentDirty.statusHebrew }, feePopup));
			setStatusPopup(false);
		}
	};

	const handleRefund = () => {
		dispatch(
			createRefund({
				currencyId: refundDirty.currencyId,
				amount: refundDirty.amount,
				refundReason: refundDirty.note,
				actionType: type,
				actionId: dataId,
				companyId,
			})
		);
		setRefundPopup(false);
		setApproveEnabled(false);
	};

	const handleResendEmail = async () => {
		const id = location.pathname.split('/').pop();
		if (canResendEmail) {
			await dispatch(resendWelcomeEmail({ companyId, id }));
			dispatch(setSuccessMessage(true));
		}
	};

	const handleFormChange = (state: any) => {
		if (type === moneyActivityTypes.PAYMENT) {
			setPaymentDirty((prev: any) => ({ ...prev, ...state.dirtyFields }));
		}
		if (state.dirtyFields?.['refund.create.currencyId']) {
			const curr = currencies.find((c: any) => c.id == state.dirtyFields['refund.create.currencyId']);
			setNoDecimalCurrency(!curr?.decimalPlaces);
		}
		setFormState(state);
	};

	// -------------------------------------------------------------------------
	// EFFECTS
	// -------------------------------------------------------------------------

	// Initial data fetching
	useEffect(() => {
		if (canViewHouseAccountBalance) {
			dispatch(getHouseAccountBalances());
		}
		if (canViewBenefits) {
			dispatch(getBenefits());
		}
	}, [canViewBenefits, canViewHouseAccountBalance, dispatch]);

	useEffect(() => {
		if (canViewBenefits && companyId) {
			dispatch(getCompanyBenefitsByCompanyId(companyId));
		}
	}, [canViewBenefits, companyId, dispatch]);

	useEffect(() => {
		if (!loading && canViewPage && companyId && !companyData && !loadedProfile.current) {
			loadedProfile.current = true;
			dispatch(getCustomerDashboard(parseInt(companyId)));
		}
	}, [companyData, canViewPage, companyId, loading, dispatch]);

	// Fetch entity data - always fetch fresh when URL params change
	const lastFetchedRef = useRef<string | null>(null);
	useEffect(() => {
		if (!loading && canViewPage && companyId && dataId) {
			const cid = parseInt(companyId),
				did = parseInt(dataId);
			// Create a unique key for this entity request
			const fetchKey = `${entityType}-${did}`;
			// Always fetch if URL params changed (new navigation), regardless of selectedEntity
			const needsFetch = lastFetchedRef.current !== fetchKey;
			if (needsFetch && ENTITY_FETCHERS[entityType]) {
				lastFetchedRef.current = fetchKey;
				dispatch(ENTITY_FETCHERS[entityType](cid, did));
			}
		}
	}, [companyId, dataId, entityType, canViewPage, loading, dispatch]);

	useEffect(() => {
		// Guard 1: Only proceed if entityType is valid and in payment category types
		if (!entityType || !PAYMENT_CATEGORY_TYPES.includes(entityType as any)) {
			return;
		}

		// Guard 2: Prevent duplicate fetches for same entityType
		if (lastFetchedEntityType.current === entityType) {
			return;
		}

		// Guard 3: Don't fetch if already loading (optional but good UX)
		if (paymentCategoryLoading) {
			return;
		}

		// Execute fetches
		dispatch(getPaymentCategories() as any);
		dispatch(fetchPaymentCategoryTranslations() as any);

		// Update guard ref to prevent re-fetch for this entityType
		lastFetchedEntityType.current = entityType;
	}, [entityType]); // Only entityType as dependency

	// Handle successful update
	useEffect(() => {
		if (successfullyUpdated) {
			dispatch(setCustomerDashboard(null));
			dispatch(setSuccessfullyUpdated(false));
			navigate(-1);
		}
	}, [successfullyUpdated, dispatch, navigate]);

	// Cleanup on unmount
	useEffect(
		() => () => {
			dispatch(setSelectedEntity(null));
		},
		[type, dispatch]
	);

	// Track original status
	useEffect(() => {
		if (formValues?.status && type !== moneyActivityTypes.CONVERSION) setOriginalStatus(formValues.status);
	}, [formValues, type]);

	// Initialize category selection
	useEffect(() => {
		if (entityType === BASIC_DATA && companyData?.basicData?.allowedPaymentCategoryIds) {
			const valid = new Set(paymentCategoryOptions.map((o) => o.id));
			const ids = companyData.basicData.allowedPaymentCategoryIds
				.map(String)
				.filter((id: string) => valid.has(id));
			setSelectedCategoryIds(ids);
			initialCategoryIds.current = ids;
		}
	}, [companyData?.basicData?.allowedPaymentCategoryIds, entityType, paymentCategoryOptions]);

	// Refund popup delay
	useEffect(() => {
		if (refundPopup) {
			const t = setTimeout(() => setApproveEnabled(true), 3000);
			return () => clearTimeout(t);
		}
	}, [refundPopup]);

	// Process form field transformations
	useEffect(() => {
		const df = formState.dirtyFields;
		if (!df) return;

		if (df.dateOfBirth) df.dateOfBirth = formatJSDate(new Date(df.dateOfBirth));
		if (df.countryId) df.country = df.country?.id;
		if (df.genderHebrew) df.gender = df.genderHebrew;

		// Extract refund fields
		['amount', 'currencyId', 'note'].forEach((k) => {
			const key = `refund.create.${k}`;
			if (_.has(df, key)) setRefundDirty((prev: any) => ({ ...prev, [k]: df[key] }));
		});

		if (formState.name === 'page.customerAccounts.forms.balance_refund.create_refund_title') {
			setIsRefundReady(!!formState.isValid);
		}
	}, [formState]);

	// Sync BO notes
	useEffect(() => {
		if (formState.values) {
			const key = 'rejectReason' in formState.values ? 'rejectReason' : 'boNotes';
			formState.dirtyFields[key] = BoNotes;
			formState.values[key] = BoNotes;
		}
	}, [BoNotes, formState]);

	// -------------------------------------------------------------------------
	// RENDER HELPERS
	// -------------------------------------------------------------------------

	const btnStyle = { borderRadius: '8px', width: '320px', height: '48px' };
	const hoverStyle = {
		...btnStyle,
		'&:hover': { background: theme.palette.root.basicDeeoGreen, boxShadow: 'none', color: 'white' },
	};

	const StatusPopup = ({ open, title, statusKey, onConfirm, onCancel }: any) => (
		<PopupComponent
			open={open}
			title={t(title)}
			subtitle={t('page.customerAccounts.forms.payment.status_change.subtitle')}
			isDefaultButton={false}
		>
			<Typography variant="h4" sx={{ fontWeight: 700 }}>
				{getStatusDisplay(statusKey, paymentDirty)}
			</Typography>
			<ButtonComponent
				variantType="contained"
				handleClick={onConfirm}
				text={t('page.customerAccounts.forms.balance_refund.create_refund_popup_approve')}
				styleObj={btnStyle}
			/>
			<ButtonComponent
				variantType="text"
				handleClick={onCancel}
				text={t('page.customerAccounts.forms.balance_refund.create_refund_popup_deny')}
				styleObj={hoverStyle}
			/>
		</PopupComponent>
	);

	const DeniedMessages = () => {
		if (type !== BENEFICIARIES || !selectedEntity?.data?.beneficiaryDeniedMessages?.length) return null;
		return (
			<>
				<Box display="flex" alignItems="center" gap={1}>
					<Avatar src="/icons/warning-orange.svg" sx={{ width: 18, height: 18 }} />
					<Typography variant="h6" sx={{ mb: 0 }}>
						{t('page.customerAccounts.forms.beneficiaries.deniedMessages.title')}
					</Typography>
				</Box>
				{_.orderBy(selectedEntity.data.beneficiaryDeniedMessages, ['createdAt'], ['desc']).map(
					(m: any, i: number) => (
						<Stack key={i} gap={1} sx={{ backgroundColor: '#F4FAF8', p: '10px' }}>
							{m.createdByBoUser && (
								<Box display="flex" alignItems="center" gap={1}>
									<img src="/icons/altshuler.svg" width={25} alt="" />
									<Typography variant="boldBody7">{`${t(
										'page.customerAccounts.forms.beneficiaries.deniedMessages.agent'
									)}: ${m.createdByBoUser.firstName} ${m.createdByBoUser.lastName}`}</Typography>
								</Box>
							)}
							{m.createdByContact && (
								<Box display="flex" alignItems="center" gap={1}>
									<img src="/icons/user-avatar.svg" width={25} alt="" />
									<Typography variant="boldBody7">{`${m.createdByContact.firstName} ${m.createdByContact.lastName}`}</Typography>
								</Box>
							)}
							<Typography variant="body1">{formatCommentDate(m.createdAt, t)}</Typography>
							<Typography variant="body1">{_.get(m, 'translation[0].he', m.message)}</Typography>
						</Stack>
					)
				)}
			</>
		);
	};

	// -------------------------------------------------------------------------
	// MAIN RENDER
	// -------------------------------------------------------------------------

	return (
		<Fragment>
			<WarningDialog
				open={hasError}
				subTitle={errorText}
				handleCancel={() => errorSetter && dispatch(errorSetter({ exist: false }))}
			/>

			{statusPopup && (
				<StatusPopup
					open
					title="page.customerAccounts.forms.payment.status_change.title"
					statusKey="statusHebrew"
					onConfirm={() => handleStatusChange()}
					onCancel={() => setStatusPopup(false)}
				/>
			)}
			{!statusPopup && feePopup && (
				<StatusPopup
					open
					title="page.customerAccounts.forms.payment_fee.status_change.title"
					statusKey="fee.statusHebrew"
					onConfirm={() => handleStatusChange(true)}
					onCancel={() => setFeePopup(false)}
				/>
			)}

			{refundPopup && (
				<PopupComponent
					open
					title={t('page.customerAccounts.forms.balance_refund.create_refund_popup_title')}
					subtitle={t('page.customerAccounts.forms.balance_refund.create_refund_popup_text')}
					isDefaultButton={false}
				>
					{Object.keys(refundDirty).length > 0 && (
						<Typography variant="h4" sx={{ fontWeight: 700 }}>
							{currencies[refundDirty.currencyId - 1]?.symbol} {formatNumber(refundDirty.amount)}
						</Typography>
					)}
					<ButtonComponent
						variantType="contained"
						disabled={!approveEnabled}
						handleClick={handleRefund}
						text={t('page.customerAccounts.forms.balance_refund.create_refund_popup_approve')}
						styleObj={btnStyle}
					/>
					<ButtonComponent
						variantType="text"
						handleClick={() => setRefundPopup(false)}
						text={t('page.customerAccounts.forms.balance_refund.create_refund_popup_deny')}
						styleObj={hoverStyle}
					/>
				</PopupComponent>
			)}

			{companyData && (
				<Page
					includeGoBack
					loading={loading || benefitLoading || balanceLoading || isEntityLoading}
					title={`${t('page.profile.' + entityType + '.title')} - ${getCompanyNameByLanguage(
						companyData?.basicData
					)}`}
					subTitle={
						<Box display="flex" justifyContent="space-between" alignItems="center">
							<span>
								{t('label.lastUpdated')}: {formatDate(item?.updatedAt || item?.updated_at || '')}
							</span>
							{type === CONTACTS && showResendBtn && canResendEmail && (
								<ButtonComponent
									handleClick={() => setEmailDialog(true)}
									text={t('page.customerAccounts.tables.contacts.resendWelcomeEmail')}
								/>
							)}
						</Box>
					}
				>
					<FormCardLayout sx={type === PLAN ? { margin: 'unset !important' } : {}}>
						{forms?.map((form: any, i: number) => (
							<FormCard key={i} cardStyle={form.cardStyle} title={t(form.name)}>
								<FormCreatorComponent
									{...form}
									translation={t}
									values={formValues}
									handleChange={handleFormChange}
									isRequireValidate={
										noDecimalCurrency &&
										form.name === 'page.customerAccounts.forms.balance_refund.create_refund_title'
									}
								/>
							</FormCard>
						))}

						{type === BENEFICIARIES && selectedEntity?.data?.allowedPaymentCategoryIds?.length > 0 && (
							<PaymentCategoriesView categoryIds={selectedEntity.data.allowedPaymentCategoryIds} />
						)}

						{type === BENEFICIARIES && selectedEntity?.data && (
							<FormCard title={t(beneficiaryHyperSyncForm.name)}>
								<FormCreatorComponent
									{...beneficiaryHyperSyncForm}
									translation={t}
									values={selectedEntity.data}
									handleChange={handleFormChange}
								/>
							</FormCard>
						)}

						{entityType === BASIC_DATA && !isInactive && canEditCompany && (
							<FormCard title={t('page.payment.category.title')}>
								<PaymentCategoryPicker
									value={selectedCategoryIds}
									onChange={setSelectedCategoryIds}
									options={paymentCategoryOptions}
									loading={paymentCategoryLoading}
									noOptions={!paymentCategoryOptions.length && !paymentCategoryLoading}
									disabled={isInactive}
									hideTitle
								/>
							</FormCard>
						)}

						<Stack spacing={2} width="100%">
							<DeniedMessages />
						</Stack>

						<Stack spacing={2} width="100%">
							{type === BENEFICIARIES && companyId && dataId && (
								<>
									<Typography
										variant="h4"
										sx={{ color: theme.palette.common.black, fontWeight: 700 }}
									>
										{t('page.customerAccounts.forms.beneficiaries.paymentTable.title')}
									</Typography>
									<BeneficiaryPaymentsTable companyId={companyId} beneficiaryId={dataId} />
								</>
							)}

							{!isInactive && type !== PLAN && (
								<Stack direction="row" justifyContent="space-between" width="100%">
									<FormButton>
										<Button onClick={() => navigate(-1)} variant="outlined">
											{t('button.back')}
										</Button>
									</FormButton>
									<FormButton>
										<Button
											variant="contained"
											disabled={saveDisabled}
											onClick={() =>
												isRejectionStatus(formState.dirtyFields)
													? setDialog(true)
													: handleSave()
											}
										>
											{t('button.ok')}
										</Button>
									</FormButton>
								</Stack>
							)}
						</Stack>
					</FormCardLayout>
					{type === PLAN && canViewBenefits ? (
						<CompanyBenefitsTable companyId={companyId || ''} />
					) : (
						<Fragment />
					)}
				</Page>
			)}

			<DialogBox
				openDialog={dialog}
				handleClose={() => setDialog(false)}
				title={t('label.contactBlockTitle')}
				handleApprove={() => {
					if (_.isEmpty(BoNotes)) {
						setBoNotes('');
						return false;
					}
					handleSave();
					setDialog(false);
				}}
				handleReject={() => {
					const key = formState.values && 'rejectReason' in formState.values ? 'rejectReason' : 'boNotes';
					if (formState.dirtyFields) formState.dirtyFields[key] = null;
					if (formState.values) formState.values[key] = null;
					if (formState.dirtyFields) formState.dirtyFields.status = originalStatus;
					if (formState.values) formState.values.status = originalStatus;
					setDialog(false);
					setBoNotes(null);
				}}
			>
				<Stack>
					<Typography>{t('label.rejectQuestionSubtitle')}</Typography>
					<TextArea
						valid={BoNotes === null || BoNotes !== ''}
						cField={{
							type: fieldType.text,
							mode: fieldMode.readWithEnableWrite,
							props: { name: 'bo_notes', label: '', required: true, value: BoNotes ?? '' },
							validation: [{ requierd: true }],
						}}
						handleChange={(e: any) => setBoNotes(e.target.value)}
						initialValidation={[]}
					/>
				</Stack>
			</DialogBox>

			<ResendWelcomeEmailDialog
				openDialog={emailDialog}
				setOpenDialog={setEmailDialog}
				handleApprove={handleResendEmail}
			/>
		</Fragment>
	);
};
